// HSSNA fetch 客户端封装
// 规格: SPEC §4.1 API 客户端架构
//
// 职责:
//   - 封装 fetch, 自动应用请求/响应拦截器
//   - 提供 401 重试回调 (供响应拦截器调用)
//   - 标准化 headers (HeadersInit → Record<string, string>)
//
// 不重复实现拦截器逻辑 — 仅编排拦截器与 fetch 的执行顺序

import type { Interceptors, RequestConfig } from './interceptors.js';

/**
 * 创建 fetch 客户端, 自动应用拦截器
 *
 * @param interceptors createInterceptors 返回的拦截器集合
 * @param fetchImpl    自定义 fetch 实现 (默认 globalThis.fetch, 测试/SSR 可注入)
 * @returns            fetch 函数 (url, init?) => Promise<Response>
 *
 * @example
 * ```ts
 * const interceptors = createInterceptors({ authClient, service: 'medical-cws' });
 * const apiFetch = createFetchClient(interceptors);
 *
 * // GET 请求 (自动注入 JWT / Trace-Id / Request-Id / User-Agent)
 * const res = await apiFetch('/v1/alerts');
 *
 * // POST 请求 (额外注入 Idempotency-Key)
 * const res = await apiFetch('/v1/alerts/ack', {
 *   method: 'POST',
 *   headers: { 'Content-Type': 'application/json' },
 *   body: JSON.stringify({ alert_id: 'a-123' }),
 * });
 * ```
 */
export function createFetchClient(
  interceptors: Interceptors,
  // ★ 绑定 fetch 到 globalThis, 避免 "Illegal invocation" 错误
  // (默认参数 globalThis.fetch 调用时 this 上下文丢失)
  fetchImpl: typeof fetch = globalThis.fetch.bind(globalThis),
): (url: string, init?: RequestInit) => Promise<Response> {
  return async (url, init) => {
    // 1. 构建初始 RequestConfig (标准化 headers)
    const config: RequestConfig = {
      ...(init ?? {}),
      url,
      headers: normalizeHeaders(init?.headers),
    };

    // 2. 运行请求拦截器 (注入 JWT / Trace-Id / Request-Id / Idempotency-Key 等)
    const interceptedConfig = await interceptors.request(config);

    // 3. 发送请求
    let response = await fetchImpl(
      interceptedConfig.url,
      toFetchInit(interceptedConfig),
    );

    // 4. 重试回调: 使用 interceptedConfig 的最新状态重新发送, 并重新走响应拦截器
    //    响应拦截器可在 401 时更新 interceptedConfig.headers.Authorization 后调用 retry
    //    P1-8 修复: 重试后的响应也需经过 interceptors.response, 确保 RFC 7807 解析
    //    与 dispatchGlobalError 生效; _retryCount 已由首次拦截器递增, 不会无限循环
    const retry = async (): Promise<Response> => {
      const retryResponse = await fetchImpl(
        interceptedConfig.url,
        toFetchInit(interceptedConfig),
      );
      return interceptors.response(retryResponse, interceptedConfig, retry);
    };

    // 5. 运行响应拦截器 (401 refresh + 重试, RFC 7807 解析, IDOR 上报)
    response = await interceptors.response(
      response,
      interceptedConfig,
      retry,
    );

    return response;
  };
}

/**
 * 将 HeadersInit (Headers | string[][] | Record<string, string>) 规范化为 Record
 * 便于拦截器读写 header
 */
function normalizeHeaders(headers: HeadersInit | undefined): Record<string, string> {
  const result: Record<string, string> = {};
  if (!headers) return result;

  if (headers instanceof Headers) {
    headers.forEach((value, key) => {
      result[key] = value;
    });
  } else if (Array.isArray(headers)) {
    for (const pair of headers) {
      const [key, value] = pair;
      result[key] = value;
    }
  } else {
    for (const [key, value] of Object.entries(headers)) {
      result[key] = value;
    }
  }
  return result;
}

/**
 * 从 RequestConfig 提取标准 RequestInit (剔除 url / _retryCount 等非标准字段)
 */
function toFetchInit(config: RequestConfig): RequestInit {
  return {
    method: config.method,
    headers: config.headers,
    body: config.body,
    cache: config.cache,
    credentials: config.credentials,
    integrity: config.integrity,
    keepalive: config.keepalive,
    mode: config.mode,
    redirect: config.redirect,
    referrer: config.referrer,
    referrerPolicy: config.referrerPolicy,
    signal: config.signal,
    window: config.window,
  };
}
